import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  observerAuth,
  firebaseReady,
  signOutObserver,
} from "../services/firebaseAuth";
import { clearAnalyticsIdentity } from "../services/analytics";
import { observerBackendOrigin } from "../services/runtimeConfig";

export default function AccountDeletionPage() {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const user = firebaseReady ? observerAuth().currentUser : null;

  const removeAccount = async () => {
    if (!user || !confirmed) return;
    setBusy(true);
    setMessage("");
    try {
      const token = await user.getIdToken(true);
      const response = await fetch(`${observerBackendOrigin}/api/observer/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok)
        throw new Error("The account could not be deleted. Please try again.");
      clearAnalyticsIdentity();
      await signOutObserver();
      localStorage.removeItem("observer_subject_session");
      localStorage.removeItem("observer_review_session");
      setMessage("Your account and eligible app data have been deleted.");
    } catch (reason) {
      setMessage((reason as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article style={{ maxWidth: 760, margin: "0 auto", padding: "90px 22px" }}>
      <span className="section-kicker">Observer Theory</span>
      <h1>Delete your account</h1>
      <p>
        This permanently removes your sign-in, devices and eligible membership
        data and anonymizes your claim association. Accurate public-source facts,
        another person's lawful content, and restricted fraud, moderation,
        dispute, audit, backup or legal records may remain under the published
        retention policy.
      </p>
      <p>
        Deleting your Observer Theory account does not cancel a subscription
        billed by Apple or Google. Cancel it first in your store account: {" "}
        <a href="https://apps.apple.com/account/subscriptions">Apple subscriptions</a>
        {" or "}
        <a href="https://play.google.com/store/account/subscriptions?package=com.observertheory.app">
          Google Play subscriptions
        </a>
        .
      </p>
      {!user ? (
        <p>
          <Link to="/sign-in?returnTo=%2Faccount-deletion">Sign in</Link> to
          delete your account in the app. You may also contact
          hello@observertheory.com if you cannot access your sign-in method.
        </p>
      ) : message.includes("have been deleted") ? (
        <p role="status">{message}</p>
      ) : (
        <>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I understand this action is permanent and want to delete my account.
          </label>
          <button
            type="button"
            disabled={!confirmed || busy}
            onClick={removeAccount}
            style={{ marginTop: 20, border: "1px solid #8f1d1d" }}
          >
            {busy ? "Deleting…" : "Permanently delete account"}
          </button>
          {message && <p role="alert">{message}</p>}
        </>
      )}
    </article>
  );
}
